// =============================
// File: PrivacyPolicy.js
// =============================

import React, { useEffect as useEffect3 } from "react";
import MKBox3 from "components/MKBox";
import MKTypography3 from "components/MKTypography";
import MKButton3 from "components/MKButton";
import {
  Container as Container3,
  Grid as Grid3,
  Card as Card3,
  Divider as Divider3,
  List as List3,
  ListItem as ListItem3,
  ListItemText as ListItemText3,
  Link as Link3,
} from "@mui/material";

const BackToTop3 = () => (
  <MKBox3 textAlign="right" my={4}>
    <MKButton3 component="a" href="#top" variant="gradient" color="info">
      Back to top
    </MKButton3>
  </MKBox3>
);

export function PrivacyPolicyPage() {
  const company = "The Best Helper";
  const lastUpdated = "August 30, 2025";

  useEffect3(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: "intro", label: "Introduction" },
    { id: "scope", label: "Scope & Controller" },
    { id: "data_we_collect", label: "Data We Collect" },
    { id: "how_we_use", label: "How We Use Data" },
    { id: "legal_basis", label: "Legal Basis" },
    { id: "sharing", label: "Sharing with Third Parties" },
    { id: "intl_transfers", label: "International Transfers" },
    { id: "retention", label: "Data Retention" },
    { id: "security", label: "Security Measures" },
    { id: "cookies", label: "Cookies & Tracking" },
    { id: "your_rights", label: "Your Rights" },
    { id: "minors", label: "Children’s Privacy" },
    { id: "marketing", label: "Marketing Communications" },
    { id: "changes", label: "Changes to this Policy" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <MKBox3 id="top">
      <MKBox3
        sx={{
          background: "linear-gradient(135deg, #003060 0%, #0062a8 60%, #0b1220 100%)",
          color: "#fff",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container3 maxWidth="lg">
          <MKTypography3 variant="h2" color="white" fontWeight="bold" mb={1}>
            Privacy Policy
          </MKTypography3>
          <MKTypography3 variant="body1" color="white" sx={{ opacity: 0.9 }}>
            How {company} collects, uses, and protects your information across Rwanda, Kenya,
            Uganda, and Tanzania.
          </MKTypography3>
        </Container3>
      </MKBox3>

      <Container3 maxWidth="lg">
        <Grid3 container spacing={3} sx={{ my: { xs: 2, md: 4 } }}>
          <Grid3 item xs={12} md={4} lg={3}>
            <Card3 sx={{ position: { md: "sticky" }, top: { md: 32 }, p: 2, borderRadius: 3 }}>
              <MKTypography3 variant="h6" fontWeight="bold" mb={1}>
                Table of Contents
              </MKTypography3>
              <Divider3 sx={{ mb: 1 }} />
              <List3 dense>
                {sections.map((s) => (
                  <ListItem3 key={s.id} sx={{ py: 0 }}>
                    <Link3 href={`#${s.id}`} underline="hover">
                      <ListItemText3 primary={s.label} />
                    </Link3>
                  </ListItem3>
                ))}
              </List3>
              <Divider3 sx={{ my: 1 }} />
              <MKTypography3 variant="caption" color="text">
                Last updated: {lastUpdated}
              </MKTypography3>
            </Card3>
          </Grid3>

          <Grid3 item xs={12} md={8} lg={9}>
            <Card3 sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }}>
              <PSection id="intro" title="1. Introduction">
                <p>
                  This Privacy Policy explains how {company} ("we", "us") collects and processes
                  personal data when you visit our website, submit a request, or otherwise interact
                  with our services.
                </p>
              </PSection>

              <PSection id="scope" title="2. Scope & Controller">
                <p>
                  This Policy applies to the Platform operated by {company} within the East African
                  Community. Where required by law, {company} acts as the data controller
                  responsible for deciding how personal data is processed.
                </p>
              </PSection>

              <PSection id="data_we_collect" title="3. Data We Collect">
                <ul>
                  <li>
                    <strong>Identity:</strong> name, email, phone number.
                  </li>
                  <li>
                    <strong>Preferences:</strong> housing needs, vehicle type, driver needs, land
                    budget, job role.
                  </li>
                  <li>
                    <strong>Location:</strong> city/country to service your request.
                  </li>
                  <li>
                    <strong>Technical:</strong> IP address, device, browser, pages viewed,
                    timestamps.
                  </li>
                  <li>
                    <strong>Communications:</strong> messages, call notes, and support interactions.
                  </li>
                  <li>
                    <strong>Documents (optional):</strong> IDs or permits you choose to share for
                    verification.
                  </li>
                </ul>
              </PSection>

              <PSection id="how_we_use" title="4. How We Use Data">
                <ul>
                  <li>
                    Provide, coordinate, and personalize housing, transport, land, and staffing
                    matches.
                  </li>
                  <li>Communicate quotes, confirmations, reminders, and support.</li>
                  <li>Improve Platform performance, security, and user experience.</li>
                  <li>Comply with legal obligations (e.g., fraud prevention, record‑keeping).</li>
                </ul>
              </PSection>

              <PSection id="legal_basis" title="5. Legal Basis">
                <ul>
                  <li>
                    <strong>Consent</strong> (e.g., for marketing).
                  </li>
                  <li>
                    <strong>Contract</strong> (to fulfill your requests).
                  </li>
                  <li>
                    <strong>Legitimate interests</strong> (service improvement, security).
                  </li>
                  <li>
                    <strong>Legal obligation</strong> (where authorities require records).
                  </li>
                </ul>
              </PSection>

              <PSection id="sharing" title="6. Sharing with Third Parties">
                <p>
                  We may share necessary information with vetted providers (landlords, agents,
                  drivers, land sellers, or domestic staff) to fulfill your request. We do not sell
                  your personal data.
                </p>
              </PSection>

              <PSection id="intl_transfers" title="7. International Transfers">
                <p>
                  Where data moves across borders within or outside the EAC, we take reasonable
                  steps to ensure appropriate safeguards consistent with applicable laws.
                </p>
              </PSection>

              <PSection id="retention" title="8. Data Retention">
                <p>
                  We retain data only for as long as necessary to provide services and meet legal or
                  accounting requirements. Retention periods depend on request type and
                  jurisdiction.
                </p>
              </PSection>

              <PSection id="security" title="9. Security Measures">
                <p>
                  We implement administrative, technical, and physical safeguards (access controls,
                  encryption in transit, need‑to‑know access) to protect data against unauthorized
                  access, alteration, or loss.
                </p>
              </PSection>

              <PSection id="cookies" title="10. Cookies & Tracking">
                <p>
                  We use cookies and similar technologies to operate the Platform and analyze usage.
                  You can manage cookies through your browser settings. Some features may not
                  function without essential cookies.
                </p>
              </PSection>

              <PSection id="your_rights" title="11. Your Rights">
                <ul>
                  <li>Access, rectification, erasure, and portability where applicable.</li>
                  <li>Object to or restrict certain processing.</li>
                  <li>Withdraw consent for marketing at any time.</li>
                  <li>
                    Lodge a complaint with your local data protection authority where available.
                  </li>
                </ul>
              </PSection>

              <PSection id="minors" title="12. Children’s Privacy">
                <p>
                  Our services are intended for adults (18+). We do not knowingly collect data from
                  children.
                </p>
              </PSection>

              <PSection id="marketing" title="13. Marketing Communications">
                <p>
                  With your consent (or as permitted by law), we may send updates about services in
                  Rwanda, Kenya, Uganda, and Tanzania. You can unsubscribe via the link in any email
                  or by contacting us.
                </p>
              </PSection>

              <PSection id="changes" title="14. Changes to this Policy">
                <p>
                  We may update this Policy periodically. Material changes will be posted on the
                  Platform with a new effective date. Continued use signifies acceptance.
                </p>
              </PSection>

              <PSection id="contact" title="15. Contact">
                <p>
                  To exercise rights or ask questions, email: privacy@thebesthelper.example or use
                  our contact form.
                </p>
              </PSection>

              <BackToTop3 />
            </Card3>
          </Grid3>
        </Grid3>
      </Container3>
    </MKBox3>
  );
}

function PSection({ id, title, children }) {
  return (
    <MKBox3 id={id} mb={4}>
      <MKTypography3 variant="h5" fontWeight="bold" mb={1}>
        {title}
      </MKTypography3>
      <Divider3 sx={{ mb: 2 }} />
      <MKTypography3 variant="body2" color="text" component="div" sx={{ lineHeight: 1.8 }}>
        {children}
      </MKTypography3>
    </MKBox3>
  );
}
